import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Icons from '../svgs/icons';
import IconSelector from './IconSelector';
import useSalarySacrificeIcons from '../../hooks/useSalarySacrificeIcons';

const PlanDetailForm = ({
  planDetail,
  index,
  onChange,
  onRemove,
  disabled = false,
  isExpanded = false,
  onToggleExpand
}) => {
  const [localErrors, setLocalErrors] = useState({});
  const { icons, loading: iconsLoading } = useSalarySacrificeIcons();

  const handleInputChange = (field, value) => {
    onChange(index, field, value);
    // Clear error when user starts typing
    if (localErrors[field]) {
      setLocalErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validatePlan = () => {
    const errors = {};

    if (!planDetail.title?.trim()) {
      errors.title = 'Plan title is required';
    }

    if (!planDetail.description?.trim()) {
      errors.description = 'Description is required';
    }

    setLocalErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormBlur = () => {
    if (!disabled) {
      validatePlan();
    }
  };

  if (!isExpanded && !disabled) {
    // Collapsed view for editing
    return (
      <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-2xl p-4">
        <div className="flex justify-between items-center">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              {/* Show selected icon in collapsed view */}
              {planDetail.icon && icons.length > 0 && (
                <div className="flex-shrink-0 w-10 h-10 rounded-lg border-2 border-shoorah-primary bg-shoorah-primary/10 p-1.5">
                  {(() => {
                    const selectedIcon = icons.find((icon) => icon.name === planDetail.icon);
                    console.log('Selected Icon:', selectedIcon);
                    return selectedIcon?.icon_url ? (
                      <img
                        src={selectedIcon.icon_url}
                        alt="Selected icon"
                        className="w-full h-full object-contain"
                      />
                    ) : null;
                  })()}
                </div>
              )}
              <div className="flex-1">
                <h4 className="font-medium text-gray-900 dark:text-white">
                  {planDetail.title || 'New Plan Detail'}
                </h4>
                {planDetail.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                    {planDetail.description}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <button
              onClick={() => onToggleExpand(index)}
              className="p-2 text-shoorah-primary hover:text-shoorah-secondary transition-colors"
              title="Edit plan detail"
            >
              <Icons.edit className="w-5 h-5" />
            </button>
            <button
              onClick={() => onRemove(index)}
              className="p-2 text-red-500 hover:text-red-700 transition-colors"
              title="Remove plan detail"
            >
              <Icons.trash className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (disabled) {
    // Read-only view
    return (
      <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-2xl p-6">
        <div className="flex items-start gap-4">
          {/* Show selected icon in read-only view */}
          {planDetail.icon && icons.length > 0 && (
            <div className="flex-shrink-0 w-14 h-14 rounded-lg border-2 border-shoorah-primary bg-shoorah-primary/10 p-2">
              {(() => {
                const selectedIcon = icons.find((icon) => icon.name === planDetail.icon);
                return selectedIcon?.icon_url ? (
                  <img
                    src={selectedIcon.icon_url}
                    alt="Plan icon"
                    className="w-full h-full object-contain"
                  />
                ) : null;
              })()}
            </div>
          )}

          <div className="flex-1">
            <h4 className="font-semibold text-lg text-gray-900 dark:text-white mb-3">
              {planDetail.title}
            </h4>

            {planDetail.description && (
              <p className="text-gray-700 dark:text-gray-300">{planDetail.description}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Expanded edit form
  return (
    <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-2xl p-6 space-y-4">
      {/* Header with collapse and remove buttons */}
      <div className="flex justify-between items-center mb-4">
        <h4 className="font-medium text-gray-900 dark:text-white">
          {planDetail.title ? `Edit: ${planDetail.title}` : 'New Plan Detail'}
        </h4>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onToggleExpand(index)}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            title="Collapse"
          >
            <Icons.chevronUp className="w-5 h-5" />
          </button>
          <button
            onClick={() => onRemove(index)}
            className="p-2 text-red-500 hover:text-red-700 transition-colors"
            title="Remove plan detail"
          >
            <Icons.trash className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Plan Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Plan Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          className="w-full rounded-lg px-4 py-2 bg-shoorah-normanGray border-[1.5px] border-shoorah-borderGray dark:bg-shoorah-darkBgColor dark:border-shoorah-darkBorder focus:outline-none focus:ring-2 focus:ring-shoorah-primary"
          placeholder="e.g., Basic Health Plan"
          value={planDetail.title || ''}
          onChange={(e) => handleInputChange('title', e.target.value)}
          onBlur={handleFormBlur}
        />
        {localErrors.title && <p className="text-red-500 text-sm mt-1">{localErrors.title}</p>}
      </div>

      {/* Icon Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Select Icon (Optional)
        </label>
        <IconSelector
          icons={icons}
          selectedIconId={planDetail.icon}
          onSelect={(iconId) => handleInputChange('icon', iconId)}
          disabled={disabled}
          loading={iconsLoading}
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Description <span className="text-red-500">*</span>
        </label>
        <textarea
          className="w-full rounded-lg px-4 py-2 bg-shoorah-normanGray border-[1.5px] border-shoorah-borderGray dark:bg-shoorah-darkBgColor dark:border-shoorah-darkBorder focus:outline-none focus:ring-2 focus:ring-shoorah-primary resize-none"
          placeholder="Enter detailed plan description"
          rows="4"
          value={planDetail.description || ''}
          onChange={(e) => handleInputChange('description', e.target.value)}
          onBlur={handleFormBlur}
        />
        {localErrors.description && (
          <p className="text-red-500 text-sm mt-1">{localErrors.description}</p>
        )}
      </div>
    </div>
  );
};

PlanDetailForm.propTypes = {
  planDetail: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    icon: PropTypes.string
  }).isRequired,
  index: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isExpanded: PropTypes.bool,
  onToggleExpand: PropTypes.func
};

PlanDetailForm.defaultProps = {
  disabled: false,
  isExpanded: false,
  onToggleExpand: () => {}
};

export default PlanDetailForm;
